/**
 * The livery material: one TSL node graph (CLAUDE.md: no ShaderMaterial, no onBeforeCompile)
 * that paints a mech from the masks the asset pipeline bakes into its vertices:
 *
 *   COLOR_0  (primary, secondary, accent, baked AO)          -> attribute('color')
 *   _WEAR    (convex edge wear, emissive, decal mask, cavity) -> attribute('_wear')
 *   TEXCOORD_1 the planar decal projection                   -> uv(1)
 *
 * Paint = the three livery colours by mask plus bare steel for the rest, modulated by the detail
 * texture (the body's desaturated albedo or the kit's metal set); the stencil decal is sampled
 * from the atlas where the decal mask is set; worn edges blend to bare metal (rougher paint,
 * shinier metal); AO and the cavity mask darken the ambient term; the emissive mask glows in the
 * emissive colour, driven by heat, with an accent flash on hits. The cloak variant is the same
 * graph made transparent with a rim. One instance serves both the body and the kit materials of
 * a mech, so the uniforms are shared and a livery change never recompiles anything.
 */
import type { MechLiveryDefaults } from '@steelwake/shared/data';
import {
  attribute,
  color,
  distance,
  exp,
  float,
  int,
  mix,
  normalView,
  positionGeometry,
  positionViewDirection,
  select,
  smoothstep,
  texture,
  uniform,
  uniformArray,
  uv,
  vec3,
  type vec4,
} from 'three/tsl';
import {
  Color,
  MeshStandardNodeMaterial,
  Vector2,
  Vector4,
  type MeshStandardMaterial,
  type Node,
  type Texture,
} from 'three/webgpu';

import { decalTileOffset } from './look.js';

export interface DecalAtlas {
  texture: Texture;
  columns: number;
  /** Decal ids in tile order (row-major from the top-left). */
  tiles: readonly string[];
}

/** The texture slots a livery material takes over from a loaded glTF material. */
export interface LiveryMaps {
  map: Texture | null;
  normalMap: Texture | null;
  normalScale: Vector2;
}

export function liveryMapsOf(material: MeshStandardMaterial): LiveryMaps {
  return {
    map: material.map,
    normalMap: material.normalMap,
    normalScale: material.normalScale.clone(),
  };
}

export interface LiveryMaterialSet {
  opaque: MeshStandardNodeMaterial;
  cloak: MeshStandardNodeMaterial;
}

/** Linear value the detail textures average around (sRGB 0.5), so they modulate the paint about 1. */
const DETAIL_MEAN = 0.214;
const DETAIL_STRENGTH = 0.5;
/** Unpainted structure (where no paint mask is set). */
const STEEL = 0x3a3d42;
/** Metal showing through worn paint. */
const BARE = 0x8d9298;

/** A colour node is a vec3 at runtime; the typings only let colours scale by floats. */
function rgb(node: Node<'color'>): Node<'vec3'> {
  return node as unknown as Node<'vec3'>;
}

/** Hits a mech remembers as scorch marks (Phase 8); the oldest is replaced. */
export const SCORCHES = 8;
/** Fringe ages past this are invisible; the slot is free. */
const COLD = 100;

interface ScorchArray {
  array: Vector4[];
  element(index: unknown): ReturnType<typeof vec4>;
}

interface AgeArray {
  array: number[];
  element(index: unknown): ReturnType<typeof float>;
}

export class Livery {
  readonly body: LiveryMaterialSet;
  readonly kit: LiveryMaterialSet;
  private readonly uPrimary = uniform(new Color());
  private readonly uSecondary = uniform(new Color());
  private readonly uAccent = uniform(new Color());
  private readonly uEmissive = uniform(new Color());
  private readonly uDecalTint = uniform(new Color());
  private readonly uDecalOffset = uniform(new Vector2());
  private readonly uDecalOn = uniform(0);
  private readonly uWear = uniform(0);
  /** Multiplier on the emissive mask (1 at rest, up with heat). */
  private readonly uGlow = uniform(1);
  /** Whole-surface accent emission (hit flash, a trace of heat). */
  private readonly uFlash = uniform(0);
  /** Bind-space hit points (xyz) and radii (w); a zero radius is an empty slot. */
  private readonly uScorch = uniformArray(
    Array.from({ length: SCORCHES }, () => new Vector4()),
    'vec4',
  ) as unknown as ScorchArray;
  /** Seconds since each scorch landed (the accent fringe fades with it). */
  private readonly uScorchAge = uniformArray(
    Array.from({ length: SCORCHES }, () => COLD),
    'float',
  ) as unknown as AgeArray;
  private scorchCursor = 0;
  private state: MechLiveryDefaults;

  constructor(
    private readonly atlas: DecalAtlas,
    maps: { body: LiveryMaps; kit: LiveryMaps },
    livery: MechLiveryDefaults,
  ) {
    this.body = {
      opaque: this.build('livery-body', maps.body, false),
      cloak: this.build('livery-body-cloak', maps.body, true),
    };
    this.kit = {
      opaque: this.build('livery-kit', maps.kit, false),
      cloak: this.build('livery-kit-cloak', maps.kit, true),
    };
    this.state = livery;
    this.set(livery);
  }

  /** Re-points the uniforms; no material is rebuilt. */
  set(livery: MechLiveryDefaults): void {
    this.uPrimary.value.set(livery.primary);
    this.uSecondary.value.set(livery.secondary);
    this.uAccent.value.set(livery.accent);
    this.uEmissive.value.set(livery.emissive);
    this.uWear.value = livery.wear;
    const index = livery.decal ? this.atlas.tiles.indexOf(livery.decal.id) : -1;
    this.uDecalOn.value = index >= 0 ? 1 : 0;
    const [u, v] = decalTileOffset(Math.max(index, 0), this.atlas.columns);
    this.uDecalOffset.value.set(u, v);
    if (livery.decal) this.uDecalTint.value.set(livery.decal.tint);
    this.state = livery;
  }

  current(): MechLiveryDefaults {
    return this.state;
  }

  /** Heat as a fraction of the cap and the hit flash (0..1) drive the glow. */
  setGlow(heat: number, flash: number): void {
    this.uGlow.value = 1 + heat * 2.5;
    this.uFlash.value = flash * 0.8 + heat * 0.06;
  }

  /** Records a hit at a bind-space point; the oldest of eight marks is replaced. */
  addScorch(point: { x: number; y: number; z: number }, radius: number): void {
    const slot = this.scorchCursor;
    this.scorchCursor = (this.scorchCursor + 1) % SCORCHES;
    this.uScorch.array[slot]?.set(point.x, point.y, point.z, radius);
    this.uScorchAge.array[slot] = 0;
  }

  clearScorches(): void {
    for (const v of this.uScorch.array) v.set(0, 0, 0, 0);
    this.uScorchAge.array.fill(COLD);
  }

  /** How many marks are held. */
  scorchCount(): number {
    return this.uScorch.array.filter((v) => v.w > 0).length;
  }

  /** Ages the fringes (called once per frame). */
  update(dt: number): void {
    for (let i = 0; i < SCORCHES; i++) {
      this.uScorchAge.array[i] = Math.min(COLD, (this.uScorchAge.array[i] ?? COLD) + dt);
    }
  }

  materials(): MeshStandardNodeMaterial[] {
    return [this.body.opaque, this.body.cloak, this.kit.opaque, this.kit.cloak];
  }

  dispose(): void {
    for (const m of this.materials()) m.dispose();
  }

  private build(name: string, maps: LiveryMaps, cloak: boolean): MeshStandardNodeMaterial {
    const material = new MeshStandardNodeMaterial();
    material.name = name;
    const masks = attribute<'vec4'>('color', 'vec4');
    const wear = attribute<'vec4'>('_wear', 'vec4');

    const painted = masks.r.add(masks.g).add(masks.b).clamp(0, 1);
    const paint = this.uPrimary
      .mul(masks.r)
      .add(this.uSecondary.mul(masks.g))
      .add(this.uAccent.mul(masks.b))
      .add(color(STEEL).mul(painted.oneMinus()));
    const detail: Node<'vec3'> = maps.map
      ? texture(maps.map, uv())
          .rgb.div(DETAIL_MEAN)
          .sub(1)
          .mul(DETAIL_STRENGTH)
          .add(1)
          .clamp(0.25, 2)
      : vec3(1);
    const tileUv = uv(1).div(this.atlas.columns).add(this.uDecalOffset);
    const stencil = texture(this.atlas.texture, tileUv).a.mul(wear.b).mul(this.uDecalOn);
    const worn = smoothstep(this.uWear.oneMinus(), float(1.0001), wear.r);
    const albedo = mix(
      mix(paint, rgb(this.uDecalTint), stencil).mul(detail),
      rgb(color(BARE)).mul(detail),
      worn,
    );

    // Scorch marks (Phase 8): darker, rougher paint inside each hit's radius, with an accent
    // fringe that fades over the first second after the hit. Unrolled: eight distance checks
    // against the bind-pose position, so a mark stays on the plate it burned.
    let burn: Node<'float'> = float(0);
    let fringe: Node<'float'> = float(0);
    for (let i = 0; i < SCORCHES; i++) {
      const s = this.uScorch.element(int(i));
      const age = this.uScorchAge.element(int(i));
      const mark = select(
        s.w.greaterThan(0.0001),
        smoothstep(s.w.mul(0.3), s.w, distance(positionGeometry, s.xyz)).oneMinus(),
        float(0),
      );
      burn = burn.add(mark);
      fringe = fringe.add(mark.mul(exp(age.mul(-1.6))));
    }
    const scorched = burn.clamp(0, 1);
    material.colorNode = mix(albedo, vec3(0.05, 0.04, 0.035), scorched.mul(0.85));
    material.roughnessNode = mix(mix(float(0.58), float(0.34), worn), float(0.92), scorched);
    material.metalnessNode = mix(float(0.15), float(0.85), worn);
    material.aoNode = masks.a.mul(wear.a.mul(0.5).oneMinus());
    const glow = this.uEmissive
      .mul(wear.g)
      .mul(this.uGlow)
      .add(this.uAccent.mul(this.uFlash))
      .add(this.uAccent.mul(fringe.clamp(0, 1)).mul(1.4));
    if (cloak) {
      const rim = normalView.dot(positionViewDirection).abs().oneMinus().pow(2);
      material.transparent = true;
      material.depthWrite = false;
      material.opacityNode = float(0.1).add(rim.mul(0.4));
      material.emissiveNode = glow.add(this.uAccent.mul(rim).mul(0.6));
    } else {
      material.emissiveNode = glow;
    }
    if (maps.normalMap) {
      material.normalMap = maps.normalMap;
      material.normalScale.copy(maps.normalScale);
    }
    return material;
  }
}
